import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl, FormattedMessage } from '../../../util/reactIntl';
import {
  getArtworkDimensions,
  calculateArtworkRatio,
  calculateDistanceScaleFactor,
  getArtworkShadow,
  getArtworkBorder,
} from '../../../util/artworkDimensions';
import {
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  InlineTextButton,
  Button,
  IconSpinner,
} from '../../../components';
import { Switch } from '../../../components/ui/switch';
import { Separator } from '../../../components/ui/separator';
import { Settings, Move, Download, Trash2, Check } from 'lucide-react';
import { createMockupStorage } from '../../../services/MockupStorageService';
import { useTrackEvent } from '../../../context/AnalyticsContext';

import css from './RoomMockupDisplay.module.css';

const RoomMockupDisplay = ({ roomImageUrl, originalImageUrl, processedImageUrl, roomMockupData, artworkImage, artworkVariants, listing, currentUser, onDelete, onEditModeChange }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { trackEvent } = useTrackEvent();
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showMockupFirst, setShowMockupFirst] = useState(roomMockupData?.showMockupFirst ?? true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isAuthenticated = !!currentUser?.id;

  // Create storage service instance
  const mockupStorage = createMockupStorage({
    isAuthenticated,
    dispatch,
    currentUser,
  });

  // Extract artwork dimensions from listing data using shared utility
  const { artworkHeightInInches } = getArtworkDimensions(listing);

  // Get actual wall height from reconstruction API if available
  const wallHeightInMeters = roomMockupData?.wallSegmentation?.mainWall?.dimensions?.heightMeters;

  // Calculate artwork ratio relative to wall height
  // Use actual measured height if available, otherwise default to 9 feet for room mockup
  const artworkRatio = calculateArtworkRatio(artworkHeightInInches, 9, wallHeightInMeters);

  // Calculate default position based on polygon center
  const getDefaultPosition = () => {
    const mainWall = roomMockupData?.wallSegmentation?.mainWall;
    if (mainWall && mainWall.points && mainWall.points.length >= 3) {
      // Calculate the center of the polygon
      const xValues = mainWall.points.map(p => p.x);
      const yValues = mainWall.points.map(p => p.y);
      const centerX = (Math.min(...xValues) + Math.max(...xValues)) / 2;
      const centerY = (Math.min(...yValues) + Math.max(...yValues)) / 2;
      return { x: centerX, y: centerY };
    }
    // Fallback to image center if no polygon data
    return { x: 0.5, y: 0.4 };
  };

  // Get saved position or use default (normalized 0-1 range)
  const savedPosition = roomMockupData?.artworkPlacement || getDefaultPosition();
  const [artworkPosition, setArtworkPosition] = useState(savedPosition);

  // Track image dimensions and position for pixel-based polygon rendering
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });

  // Calculate artwork size based on wall dimensions
  const [artworkHeight, setArtworkHeight] = useState(null);
  const [artworkWidth, setArtworkWidth] = useState(null);

  // Distance scale factor for shadow/border based on wall polygon height
  // Larger polygon = closer to wall = more pronounced shadow/border
  const [distanceScaleFactor, setDistanceScaleFactor] = useState(1);

  const containerRef = useRef(null);
  const artworkRef = useRef(null);
  const imageRef = useRef(null);
  const artworkImageRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dropdownTriggerRef = useRef(null);

  // Point-in-polygon algorithm (ray casting) - works in 0-1 normalized coordinates
  const isPointInPolygon = (point, polygon) => {
    if (!polygon || polygon.length < 3) return true; // No constraints if no valid polygon

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x; // Already in 0-1 range from API
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

      if (intersect) inside = !inside;
    }
    return inside;
  };

  const handleDelete = async () => {
    if (!window.confirm(intl.formatMessage({ id: 'RoomMockupDisplay.confirmDelete' }))) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      // Delete mockup data from appropriate storage
      await mockupStorage.delete();

      // Track mockup deleted event
      trackEvent('mockup_deleted', {
        listing_id: listing?.id?.uuid,
        listing_title: listing?.attributes?.title,
        is_authenticated: isAuthenticated,
      });

      // Notify parent component to update UI
      if (onDelete) {
        onDelete();
      }
    } catch (err) {
      console.error('Failed to delete room mockup:', err);
      setError('Failed to delete image. Please try again.');
      setDeleting(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);

    try {
      // Get the highest resolution artwork image URL for download
      // Prioritize original/highest quality variants for best download quality
      const variants = artworkImage?.attributes?.variants;
      const preferredVariants = ['original', 'scaled-xlarge', 'scaled-large', 'scaled-medium', 'scaled-small'];

      let artworkUrl = null;
      for (const variantName of preferredVariants) {
        if (variants?.[variantName]?.url) {
          artworkUrl = variants[variantName].url;
          break;
        }
      }

      // Use processed room image (roomImageUrl is already the processed version)
      const roomDownloadUrl = roomImageUrl;

      if (!roomDownloadUrl || !artworkUrl) {
        throw new Error('Missing image URLs');
      }

      // Load images
      const loadImage = (src) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });
      };

      const [roomImg, artworkImg] = await Promise.all([
        loadImage(roomDownloadUrl),
        loadImage(artworkUrl)
      ]);

      // Create canvas at 2x resolution for higher quality artwork
      const scaleFactor = 2;
      const canvas = document.createElement('canvas');
      canvas.width = roomImg.width * scaleFactor;
      canvas.height = roomImg.height * scaleFactor;
      const ctx = canvas.getContext('2d');

      // Draw room background scaled up to fill canvas
      ctx.drawImage(roomImg, 0, 0, canvas.width, canvas.height);

      // Calculate artwork dimensions and position
      // artworkPosition is in normalized 0-1 range, convert to pixels
      const artworkX = artworkPosition.x * canvas.width;
      const artworkY = artworkPosition.y * canvas.height;

      // Calculate artwork height based on wall dimensions and actual listing dimensions
      let artworkHeightPx;
      let downloadScaleFactor = 1; // Default scale factor for shadow/border
      const mainWall = roomMockupData?.wallSegmentation?.mainWall;
      if (mainWall && mainWall.points && mainWall.points.length >= 3) {
        const yValues = mainWall.points.map(p => p.y);
        const minY = Math.min(...yValues);
        const maxY = Math.max(...yValues);
        const polygonHeightNormalized = maxY - minY;
        const polygonHeightPx = polygonHeightNormalized * canvas.height;
        artworkHeightPx = artworkRatio * polygonHeightPx;

        // Calculate distance scale factor using shared utility
        downloadScaleFactor = calculateDistanceScaleFactor(polygonHeightNormalized);
      } else {
        // Fallback: 20% of image height
        artworkHeightPx = canvas.height * 0.2;
      }

      // Calculate artwork width maintaining aspect ratio
      const artworkAspectRatio = artworkImg.width / artworkImg.height;
      const artworkWidthPx = artworkHeightPx * artworkAspectRatio;

      // Calculate artwork position (top-left corner)
      const artworkLeft = artworkX - artworkWidthPx / 2;
      const artworkTop = artworkY - artworkHeightPx / 2;

      // Add shadow to artwork (base: 0.7px 0.7px 1.4px rgba(0, 0, 0, 0.3))
      // Scale shadow properties to match both canvas scale factor AND distance scale factor
      ctx.shadowOffsetX = 0.7 * scaleFactor * downloadScaleFactor;
      ctx.shadowOffsetY = 0.7 * scaleFactor * downloadScaleFactor;
      ctx.shadowBlur = 1.4 * scaleFactor * downloadScaleFactor;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';

      // Draw artwork centered at the position
      ctx.drawImage(
        artworkImg,
        artworkLeft,
        artworkTop,
        artworkWidthPx,
        artworkHeightPx
      );

      // Reset shadow for subsequent drawing
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';

      // Add bottom border to artwork (base: 0.3px solid rgba(0, 0, 0, 0.45))
      // Scale border width to match both canvas scale factor AND distance scale factor
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.lineWidth = 0.3 * scaleFactor * downloadScaleFactor;
      ctx.beginPath();
      ctx.moveTo(artworkLeft, artworkTop + artworkHeightPx);
      ctx.lineTo(artworkLeft + artworkWidthPx, artworkTop + artworkHeightPx);
      ctx.stroke();

      // Add watermark at bottom right
      const watermarkText = 'joodaic.com';
      const fontSize = Math.max(16 * scaleFactor, canvas.height * 0.025); // Responsive font size scaled
      const padding = 20 * scaleFactor;

      ctx.font = `${fontSize}px Arial, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';

      // Draw shadow/stroke for visibility
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.lineWidth = 3 * scaleFactor;
      ctx.strokeText(watermarkText, canvas.width - padding, canvas.height - padding);

      // Draw white text
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText(watermarkText, canvas.width - padding, canvas.height - padding);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Failed to generate image');
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const timestamp = new Date().getTime();
        link.download = `room-mockup-${timestamp}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);

        // Track mockup downloaded event
        trackEvent('mockup_downloaded', {
          listing_id: listing?.id?.uuid,
          listing_title: listing?.attributes?.title,
          is_authenticated: isAuthenticated,
        });

        setDownloading(false);
      }, 'image/png');
    } catch (err) {
      console.error('Failed to download image:', err);
      setError('Failed to download image. Please try again.');
      setDownloading(false);
    }
  };

  // Helper function to extract coordinates from both mouse and touch events
  const getEventCoordinates = (e) => {
    if (e.touches && e.touches.length > 0) {
      // Touch event
      return {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      // Touch end event
      return {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
      };
    } else {
      // Mouse event
      return {
        x: e.clientX,
        y: e.clientY,
      };
    }
  };

  const handleMouseDown = (e) => {
    if (!isEditMode) return;

    e.preventDefault();
    const coords = getEventCoordinates(e);
    setIsDragging(true);
    dragStartPos.current = {
      x: coords.x,
      y: coords.y,
      startPosX: artworkPosition.x,
      startPosY: artworkPosition.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !imageRef.current) return;

    e.preventDefault();
    const coords = getEventCoordinates(e);
    const image = imageRef.current;
    const imageRect = image.getBoundingClientRect();

    // Calculate movement as normalized fraction (0-1) based on IMAGE dimensions
    const deltaX = (coords.x - dragStartPos.current.x) / imageRect.width;
    const deltaY = (coords.y - dragStartPos.current.y) / imageRect.height;

    let newX = Math.max(0, Math.min(1, dragStartPos.current.startPosX + deltaX));
    let newY = Math.max(0, Math.min(1, dragStartPos.current.startPosY + deltaY));

    // Check if wall segmentation data exists and constrain to wall boundary
    const mainWall = roomMockupData?.wallSegmentation?.mainWall;
    if (mainWall && mainWall.points && mainWall.points.length >= 3) {
      // Calculate artwork edges in normalized coordinates (0-1 range)
      // Artwork dimensions are in pixels, need to normalize to 0-1 range
      if (artworkWidth && artworkHeight && imageDimensions.width > 0 && imageDimensions.height > 0) {
        // Calculate half-width and half-height in normalized coordinates
        const halfWidthNormalized = (artworkWidth / 2) / imageDimensions.width;
        const halfHeightNormalized = (artworkHeight / 2) / imageDimensions.height;

        // Calculate points along the edges of the artwork for better boundary detection
        // Check corners and midpoints of each edge
        const edgePoints = [
          // Four corners
          { x: newX - halfWidthNormalized, y: newY - halfHeightNormalized }, // Top-left
          { x: newX + halfWidthNormalized, y: newY - halfHeightNormalized }, // Top-right
          { x: newX - halfWidthNormalized, y: newY + halfHeightNormalized }, // Bottom-left
          { x: newX + halfWidthNormalized, y: newY + halfHeightNormalized }, // Bottom-right
          // Midpoints of edges
          { x: newX, y: newY - halfHeightNormalized }, // Top-center
          { x: newX, y: newY + halfHeightNormalized }, // Bottom-center
          { x: newX - halfWidthNormalized, y: newY }, // Left-center
          { x: newX + halfWidthNormalized, y: newY }, // Right-center
        ];

        // Check if all edge points are within the wall polygon
        const allPointsInside = edgePoints.every(point => isPointInPolygon(point, mainWall.points));

        if (!allPointsInside) {
          // At least one edge point is outside wall, don't update
          return;
        }
      } else {
        // Fallback to center point check if dimensions not yet calculated
        if (!isPointInPolygon({ x: newX, y: newY }, mainWall.points)) {
          return;
        }
      }
    }

    setArtworkPosition({ x: newX, y: newY });
  };

  const handleMouseUp = async () => {
    if (!isDragging) return;

    setIsDragging(false);

    // Save position to appropriate storage
    try {
      const updatedData = {
        ...roomMockupData,
        artworkPlacement: artworkPosition,
      };
      await mockupStorage.update(updatedData);
    } catch (err) {
      console.error('Failed to save artwork position:', err);
      setError('Failed to save position. Please try again.');
    }
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    if (!isEditMode) return;

    e.preventDefault();
    const coords = getEventCoordinates(e);
    setIsDragging(true);
    dragStartPos.current = {
      x: coords.x,
      y: coords.y,
      startPosX: artworkPosition.x,
      startPosY: artworkPosition.y,
    };
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !imageRef.current) return;

    e.preventDefault();
    handleMouseMove(e);
  };

  const handleTouchEnd = async (e) => {
    if (!isDragging) return;

    e.preventDefault();
    setIsDragging(false);

    // Save position to appropriate storage
    try {
      const updatedData = {
        ...roomMockupData,
        artworkPlacement: artworkPosition,
      };
      await mockupStorage.update(updatedData);
    } catch (err) {
      console.error('Failed to save artwork position:', err);
      setError('Failed to save position. Please try again.');
    }
  };

  const handleTogglePosition = async () => {
    const newShowMockupFirst = !showMockupFirst;
    setShowMockupFirst(newShowMockupFirst);

    // Save to appropriate storage
    try {
      const updatedData = {
        ...roomMockupData,
        showMockupFirst: newShowMockupFirst,
      };
      await mockupStorage.update(updatedData);
    } catch (err) {
      console.error('Failed to save mockup position preference:', err);
      setError('Failed to save preference. Please try again.');
    }
  };

  // Measure image dimensions and position, handle resize with ResizeObserver
  const updateDimensions = useCallback(() => {
    if (imageRef.current && containerRef.current) {
      const imageRect = imageRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      // Calculate the offset of the image within the container
      // This accounts for max-width/max-height centering the image
      const offsetX = imageRect.left - containerRect.left;
      const offsetY = imageRect.top - containerRect.top;

      setImageDimensions({
        width: imageRect.width,
        height: imageRect.height,
        offsetX,
        offsetY
      });
    }
  }, []);

  useEffect(() => {
    if (!imageRef.current || !containerRef.current) return;

    // Initial measurement
    updateDimensions();

    // Use ResizeObserver to watch the image element AND container for size changes
    // This ensures we catch layout changes even if the image itself doesn't resize
    // (e.g. container grows but image stays at max native size, changing centering offset)
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(imageRef.current);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateDimensions]);

  // Calculate artwork height based on wall polygon dimensions
  // Use actual artwork dimensions from listing data (artworkRatio calculated from listing height)
  useEffect(() => {
    const mainWall = roomMockupData?.wallSegmentation?.mainWall;
    if (!mainWall || !mainWall.points || mainWall.points.length < 3) {
      // No wall data, use a default size (20% of image height as fallback)
      const defaultHeight = imageDimensions.height * 0.2;
      setArtworkHeight(defaultHeight);

      // Calculate width based on artwork aspect ratio
      if (artworkImageRef.current) {
        const aspectRatio = artworkImageRef.current.naturalWidth / artworkImageRef.current.naturalHeight;
        setArtworkWidth(defaultHeight * aspectRatio);
      }

      setDistanceScaleFactor(1); // Default scale factor
      return;
    }

    // Calculate polygon height in normalized coordinates (0-1 range)
    const yValues = mainWall.points.map(p => p.y);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);
    const polygonHeightNormalized = maxY - minY;

    // Convert to pixels
    const polygonHeightPx = polygonHeightNormalized * imageDimensions.height;

    // Use actual artwork height from listing dimensions (artworkRatio = artwork height / wall height)
    const artworkHeightPx = artworkRatio * polygonHeightPx;

    setArtworkHeight(artworkHeightPx);

    // Calculate width based on artwork aspect ratio
    if (artworkImageRef.current) {
      const aspectRatio = artworkImageRef.current.naturalWidth / artworkImageRef.current.naturalHeight;
      setArtworkWidth(artworkHeightPx * aspectRatio);
    }

    // Calculate distance scale factor using shared utility
    const scaleFactor = calculateDistanceScaleFactor(polygonHeightNormalized);
    setDistanceScaleFactor(scaleFactor);
  }, [imageDimensions, roomMockupData, artworkRatio]);

  // Handle image load
  const handleImageLoad = () => {
    updateDimensions();
  };

  // Handle artwork image load - recalculate dimensions when artwork image loads
  const handleArtworkImageLoad = () => {
    if (artworkImageRef.current && artworkHeight) {
      const aspectRatio = artworkImageRef.current.naturalWidth / artworkImageRef.current.naturalHeight;
      setArtworkWidth(artworkHeight * aspectRatio);
    }
  };

  // Add/remove touchstart listener on artwork element (must be non-passive to allow preventDefault)
  useEffect(() => {
    const artworkElement = artworkRef.current;
    if (!artworkElement || !isEditMode) return;

    artworkElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    return () => {
      artworkElement.removeEventListener('touchstart', handleTouchStart);
    };
  }, [isEditMode, artworkPosition]);

  // Add/remove global mouse and touch move and up listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, artworkPosition]);

  // Sync showMockupFirst state with roomMockupData changes
  useEffect(() => {
    setShowMockupFirst(roomMockupData?.showMockupFirst ?? true);
  }, [roomMockupData?.showMockupFirst]);

  // Track mockup viewed event on mount
  useEffect(() => {
    trackEvent('mockup_viewed', {
      listing_id: listing?.id?.uuid,
      listing_title: listing?.attributes?.title,
      is_authenticated: isAuthenticated,
    });
  }, []);

  // Auto-enter edit mode on initial load if position is still at default (0.5, 0.4)
  useEffect(() => {
    console.log('[RoomMockupDisplay] Auto-edit check - isEditMode:', isEditMode);
    console.log('[RoomMockupDisplay] Current artworkPosition:', artworkPosition);
    
    // Only run on initial mount and if not already in edit mode
    if (isEditMode) {
      console.log('[RoomMockupDisplay] Already in edit mode, skipping auto-edit');
      return;
    }

    // Check if position is still at the hardcoded default (user hasn't adjusted it)
    const isAtDefault = Math.abs(artworkPosition.x - 0.5) < 0.001 && 
                        Math.abs(artworkPosition.y - 0.4) < 0.001;
    
    console.log('[RoomMockupDisplay] Position check:', {
      x: artworkPosition.x,
      y: artworkPosition.y,
      isAtDefault
    });

    // If position matches default (0.5, 0.4), automatically enter edit mode
    if (isAtDefault) {
      console.log('[RoomMockupDisplay] Position is at default (0.5, 0.4), entering edit mode');
      setIsEditMode(true);
      if (onEditModeChange) {
        onEditModeChange(true);
      }
    } else {
      console.log('[RoomMockupDisplay] Position has been adjusted, not entering edit mode');
    }
  }, []); // Empty dependency array - only run on initial mount
  
  // Get the artwork image URL from variants
  // Prefer larger variants for better quality display
  const variants = artworkImage?.attributes?.variants;
  const preferredVariants = ['scaled-large', 'scaled-medium', 'scaled-xlarge', 'scaled-small'];
  
  let artworkUrl = null;
  for (const variantName of preferredVariants) {
    if (variants?.[variantName]?.url) {
      artworkUrl = variants[variantName].url;
      break;
    }
  }

  if (!roomImageUrl || !artworkUrl) {
    return null;
  }
  
  // Use the roomImageUrl which is already the preferred image (processed or original)
  // In the future, we could add a toggle to switch between original and processed

  // Get wall boundary data for constraining artwork movement
  const mainWall = roomMockupData?.wallSegmentation?.mainWall;
  const hasWallConstraint = mainWall && mainWall.points && mainWall.points.length >= 3;

  return (
    <div className={css.root}>
      <div className={css.itemCentering}>
        <div className={css.mockupContainer} ref={containerRef}>
          {/* Room background image */}
          <img
            ref={imageRef}
            src={roomImageUrl}
            alt="Your room"
            className={css.roomImage}
            onLoad={handleImageLoad}
          />


          {/* Overlaid artwork */}
          <div
            ref={artworkRef}
            className={`${css.artworkOverlay} ${isEditMode ? css.artworkEditable : ''} ${isDragging ? css.artworkDragging : ''}`}
            style={{
              left: `${imageDimensions.offsetX + (artworkPosition.x * imageDimensions.width)}px`,
              top: `${imageDimensions.offsetY + (artworkPosition.y * imageDimensions.height)}px`,
              height: artworkHeight ? `${artworkHeight}px` : undefined,
              width: artworkWidth ? `${artworkWidth}px` : undefined,
            }}
            onMouseDown={handleMouseDown}
          >
            <img
              ref={artworkImageRef}
              src={artworkUrl}
              alt={intl.formatMessage({ id: 'RoomMockupDisplay.altText' })}
              className={css.artworkImage}
              draggable={false}
              onLoad={handleArtworkImageLoad}
              style={{
                boxShadow: getArtworkShadow(distanceScaleFactor),
                borderBottom: getArtworkBorder(distanceScaleFactor)
              }}
            />
          </div>

          {/* Controls */}
          <div className={css.controls}>
            {/* Settings menu - only show when not in edit mode */}
            {!isEditMode && (
              <Menu isOpen={isMenuOpen} onToggleActive={setIsMenuOpen}>
                <MenuLabel className="inline-flex items-center gap-2 justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 transition-colors">
                  <Settings className="w-5 h-5" />
                  <span>Settings</span>
                </MenuLabel>
                <MenuContent className={css.menuContent}>
                {/* Show mockup first toggle with Switch */}
                <MenuItem key="toggle-position">
                  <div className={css.switchMenuItem}>
                    <span className={css.switchLabel}>
                      <FormattedMessage id="RoomMockupDisplay.showMockupFirst" />
                    </span>
                    <Switch
                      checked={showMockupFirst}
                      onCheckedChange={handleTogglePosition}
                    />
                  </div>
                </MenuItem>

                {/* Separator before Edit Position - only show when not in edit mode */}
                {!isEditMode && (
                  <MenuItem key="separator-1" className={css.separatorItem}>
                    <Separator className={css.menuSeparator} />
                  </MenuItem>
                )}

                {/* Edit Position - only show when not in edit mode */}
                {!isEditMode && (
                  <MenuItem key="edit-position">
                    <InlineTextButton
                      onClick={() => {
                        setIsEditMode(true);
                        setIsMenuOpen(false);
                        if (onEditModeChange) {
                          onEditModeChange(true);
                        }
                      }}
                      className={css.menuItem}
                    >
                      <Move className={css.menuIcon} />
                      <span className={css.menuItemText}>
                        <FormattedMessage id="RoomMockupDisplay.editPosition" />
                      </span>
                    </InlineTextButton>
                  </MenuItem>
                )}

                {/* Download */}
                <MenuItem key="download">
                  <InlineTextButton
                    onClick={handleDownload}
                    disabled={downloading}
                    className={css.menuItem}
                  >
                    {downloading ? (
                      <IconSpinner className={css.menuIcon} />
                    ) : (
                      <Download className={css.menuIcon} />
                    )}
                    <span className={css.menuItemText}>
                      {downloading ? (
                        <FormattedMessage id="RoomMockupDisplay.downloading" />
                      ) : (
                        <FormattedMessage id="RoomMockupDisplay.downloadButton" />
                      )}
                    </span>
                  </InlineTextButton>
                </MenuItem>

                <MenuItem key="separator-2" className={css.separatorItem}>
                  <Separator className={css.menuSeparator} />
                </MenuItem>

                {/* Delete */}
                <MenuItem key="delete">
                  <InlineTextButton
                    onClick={handleDelete}
                    disabled={deleting}
                    className={`${css.menuItem} ${css.destructiveItem}`}
                  >
                    {deleting ? (
                      <IconSpinner className={css.menuIcon} />
                    ) : (
                      <Trash2 className={css.menuIcon} />
                    )}
                    <span className={css.menuItemText}>
                      {deleting ? (
                        <FormattedMessage id="RoomMockupDisplay.deleting" />
                      ) : (
                        <FormattedMessage id="RoomMockupDisplay.deleteButton" />
                      )}
                    </span>
                  </InlineTextButton>
                </MenuItem>
              </MenuContent>
            </Menu>
            )}

            {error && (
              <div className={css.error}>
                {error}
              </div>
            )}

            {/* Done Editing button - shown when in edit mode */}
            {isEditMode && (
              <button
                type="button"
                onClick={() => {
                  setIsEditMode(false);
                  if (onEditModeChange) {
                    onEditModeChange(false);
                  }
                }}
                className={css.doneEditingButton}
              >
                <Check className={css.menuIcon} />
                <span>
                  <FormattedMessage id="RoomMockupDisplay.doneEditing" />
                </span>
              </button>
            )}
          </div>

          {/* Anonymous user banner */}
          {!isAuthenticated && (
            <div className={css.anonymousBanner}>
              <span>💡 Sign in to save your mockup permanently</span>
            </div>
          )}

          {/* Edit mode hint */}
          {isEditMode && (
            <div className={css.editHint}>
              {hasWallConstraint ? (
                <FormattedMessage id="RoomMockupDisplay.editHintConstrained" />
              ) : (
                <FormattedMessage id="RoomMockupDisplay.editHint" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomMockupDisplay;

